import { useEffect, useRef, useState } from 'react';

const highlights = [
  { icon: 'ri-briefcase-4-line', title: 'Atuação Estratégica', desc: 'Soluções jurídicas alinhadas à estratégia e crescimento do seu negócio.' },
  { icon: 'ri-focus-3-line', title: 'Soluções Personalizadas', desc: 'Cada cliente recebe atendimento exclusivo, com análise aprofundada do seu contexto.' },
  { icon: 'ri-shield-check-line', title: 'Proteção Patrimonial', desc: 'Estruturas jurídicas sólidas para proteger e potencializar seu patrimônio.' },
  { icon: 'ri-trophy-line', title: 'Foco em Resultados', desc: 'Comprometimento total com os objetivos do cliente, do diagnóstico à execução.' },
];

export default function About() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="sobre" className="bg-kpr-cream py-28 lg:py-36" ref={ref}>
      <div className="max-w-7xl mx-auto px-8">

        {/* Top: Image + Text side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-start mb-16">
          {/* Left: Image */}
          <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
            <div className="relative">
              <div className="w-full h-[520px] overflow-hidden">
                <img
                  src="https://static.readdy.ai/image/2751d92eb47f633f00285d5acf7c03f9/26d57d317c74917f8ec912f371c215e8.jpeg"
                  alt="Escritório KPR Advogados Associados em Curitiba"
                  className="w-full h-full object-cover object-center"
                  style={{ filter: 'brightness(0.92) contrast(1.03)' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
              </div>
              <div className="absolute -bottom-5 -right-5 w-28 h-28 border border-kpr-gold/50 pointer-events-none" />
              <div className="absolute -top-5 -left-5 w-16 h-16 border border-kpr-gold/30 pointer-events-none" />
              <div className="absolute top-6 left-6 bg-kpr-dark text-white px-5 py-4 border-l-2 border-kpr-gold">
                <div className="font-serif text-kpr-gold text-3xl font-light">+15</div>
                <div className="text-white/60 text-xs mt-0.5 leading-tight tracking-wider uppercase">Anos de<br/>experiência</div>
              </div>
            </div>
          </div>

          {/* Right: Text content */}
          <div className={`transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-10 h-px bg-kpr-gold" />
              <span className="text-kpr-gold text-xs font-medium tracking-[0.25em] uppercase">Sobre o Escritório</span>
            </div>

            <h2 className="font-serif text-4xl md:text-5xl text-kpr-dark font-light leading-[1.1] mb-8">
              Advocacia empresarial com visão estratégica e compromisso com resultados.
            </h2>

            <p className="text-kpr-gray text-[15px] leading-loose mb-4 font-light">
              A <strong className="text-kpr-dark font-medium">KPR Advogados Associados</strong> é um escritório de advocacia empresarial com atuação estratégica,
              fundado com o propósito de entregar soluções jurídicas de alto valor para empresas, investidores e
              grupos empresariais de médio e grande porte.
            </p>

            <p className="text-kpr-gray text-[15px] leading-loose mb-4 font-light">
              Com sede em <strong className="text-kpr-dark font-medium">Curitiba/PR</strong> e atuação nacional, o escritório combina excelência técnica com
              profundo entendimento do ambiente de negócios, permitindo antecipar riscos, estruturar operações
              e garantir segurança jurídica em cada etapa do crescimento.
            </p>

            <p className="text-kpr-gray text-[15px] leading-loose font-light">
              Nossa atuação une formação acadêmica sólida, experiência prática e visão empresarial — entregando
              não apenas parecer jurídico, mas estratégia integrada ao seu planejamento.
            </p>
          </div>
        </div>

        {/* Bottom: 4 highlight cards spanning full width */}
        <div
          className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 transition-all duration-700 delay-400 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {highlights.map((item) => (
            <div
              key={item.title}
              className="group flex items-start gap-4 p-6 border border-kpr-light hover:border-kpr-gold/40 bg-white transition-all duration-300"
            >
              <div className="w-10 h-10 flex items-center justify-center border border-kpr-gold/30 group-hover:border-kpr-gold group-hover:bg-kpr-gold/5 shrink-0 transition-all duration-300">
                <i className={`${item.icon} text-kpr-gold text-base w-5 h-5 flex items-center justify-center`} />
              </div>
              <div>
                <div className="font-medium text-kpr-dark text-sm mb-1 tracking-wide">{item.title}</div>
                <div className="text-kpr-gray text-xs leading-relaxed font-light">{item.desc}</div>
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}
